/**
 * The `/saved` screen's persisted state (§12.4, SPEC `saved-posts`) and its translation into #66
 * params.
 *
 * `{range, filters, sort}` is stored server-side at `saved:main`. The filter vocabulary is the view
 * screen's minus `q`/`authorIds`/`saved` plus the four saved-only narrowings and the `/feed` scope
 * pair, so everything shared is sanitized and serialized by `viewState` — this module owns only what
 * is new: lists, tags, the saved-date bounds, groups/profiles, the sidebar's section arithmetic, and
 * the pruning of ids that no longer exist.
 *
 * Two deliberate departures from the feed's defaults, both because a collection defaults to showing
 * itself: the range starts at **all time**, and the sort at **Saved at, newest first**.
 */

import type { SavedFeedQuery, SavedFiltersState, SavedUiState, ViewRangeState } from '@lw/shared';
import {
  pickIdList,
  sanitizeRange,
  sanitizeSort,
  sanitizeViewFilters,
  toFilterParams,
  type ResolvedRange,
  type ViewSort,
} from '../view/viewState';

export type SavedState = {
  range: ViewRangeState;
  filters: SavedFiltersState;
  sort: ViewSort;
};

export const DEFAULT_SAVED_STATE: SavedState = {
  // A documented deviation from §5.2's 30-day default: the collection shows everything until the
  // user narrows it. The saved-date section is the sidebar's, and independent of this control.
  range: { preset: 'all' },
  filters: {},
  sort: { key: 'savedAt', dir: 'desc' },
};

/* --------------------------------------------------------------- sections */

/**
 * Sidebar sections, top to bottom (SPEC § Sidebar sections). The three collection-specific ones
 * keep the top; Groups and Profiles sit under them, ahead of the shared post filters, so the
 * sidebar reads "which saves · whose saves · which posts".
 */
export const SAVED_SECTIONS = [
  'lists',
  'tags',
  'savedDate',
  'groups',
  'profiles',
  'postTypes',
  'contentTypes',
  'performance',
  'attributes',
  'length',
] as const;
export type SavedSectionId = (typeof SAVED_SECTIONS)[number];

export const SAVED_SECTION_LABEL: Record<SavedSectionId, string> = {
  lists: 'Lists',
  tags: 'Tags',
  savedDate: 'Saved date',
  groups: 'Groups',
  profiles: 'Profiles',
  postTypes: 'Post type',
  contentTypes: 'Content type',
  performance: 'Performance',
  attributes: 'Attributes',
  length: 'Length',
};

/** Open on a fresh session: the two that are the point of the screen. */
export const DEFAULT_OPEN_SAVED_SECTIONS: SavedSectionId[] = ['lists', 'tags'];

/** What the section's chip shows — the number of *choices* made in it, as on `/feed`. */
export function sectionCount(filters: SavedFiltersState, section: SavedSectionId): number {
  switch (section) {
    case 'lists':
      return filters.listIds?.length ?? 0;
    case 'tags':
      return filters.tagIds?.length ?? 0;
    case 'savedDate':
      return (filters.savedFrom === undefined ? 0 : 1) + (filters.savedTo === undefined ? 0 : 1);
    case 'groups':
      return filters.groupIds?.length ?? 0;
    case 'profiles':
      return filters.profileIds?.length ?? 0;
    case 'postTypes':
      return filters.postTypes?.length ?? 0;
    case 'contentTypes':
      return filters.contentTypes?.length ?? 0;
    case 'performance':
      return filters.perf?.length ?? 0;
    case 'attributes':
      return (
        (filters.hasMedia === undefined ? 0 : 1) +
        (filters.edited === undefined ? 0 : 1) +
        (filters.hasLink === undefined ? 0 : 1)
      );
    case 'length':
      return filters.lenUnit !== undefined &&
        (filters.lenMin !== undefined || filters.lenMax !== undefined)
        ? 1
        : 0;
  }
}

/** Sum of the section chips — the footer's "Clear N filters". Range and sort are header state. */
export function totalFilterCount(filters: SavedFiltersState): number {
  return SAVED_SECTIONS.reduce((total, section) => total + sectionCount(filters, section), 0);
}

/** The ⓧ on a section chip: that section only, never the others. */
export function clearSection(
  filters: SavedFiltersState,
  section: SavedSectionId,
): SavedFiltersState {
  const next: SavedFiltersState = { ...filters };
  switch (section) {
    case 'lists':
      delete next.listIds;
      break;
    case 'tags':
      delete next.tagIds;
      break;
    case 'savedDate':
      delete next.savedFrom;
      delete next.savedTo;
      break;
    case 'groups':
      delete next.groupIds;
      break;
    case 'profiles':
      delete next.profileIds;
      break;
    case 'postTypes':
      delete next.postTypes;
      break;
    case 'contentTypes':
      delete next.contentTypes;
      break;
    case 'performance':
      delete next.perf;
      break;
    case 'attributes':
      delete next.hasMedia;
      delete next.edited;
      delete next.hasLink;
      break;
    case 'length':
      delete next.lenUnit;
      delete next.lenMin;
      delete next.lenMax;
      break;
  }
  return next;
}

/** The four id vocabularies the sidebar can name, as they currently exist. */
export type LiveSavedIds = {
  lists: ReadonlySet<number>;
  tags: ReadonlySet<number>;
  groups: ReadonlySet<number>;
  /** **Every** profile, archived included: archiving does not remove a save (§6.6). */
  profiles: ReadonlySet<number>;
};

/**
 * Drops selected ids that no longer exist, so the chips stay honest after a delete. Returns `null`
 * when nothing changed — the caller must not write on every load. The server ignores dangling ids
 * regardless; this is about what the sidebar claims.
 */
export function pruneSelection(
  filters: SavedFiltersState,
  live: LiveSavedIds,
): SavedFiltersState | null {
  const keys = [
    ['listIds', live.lists],
    ['tagIds', live.tags],
    ['groupIds', live.groups],
    ['profileIds', live.profiles],
  ] as const;

  const next: SavedFiltersState = { ...filters };
  let changed = false;
  for (const [key, liveIds] of keys) {
    const current = filters[key];
    if (current === undefined) continue;
    const kept = current.filter((id) => liveIds.has(id));
    if (kept.length === current.length) continue;
    changed = true;
    if (kept.length === 0) delete next[key];
    else next[key] = kept;
  }
  return changed ? next : null;
}

/* --------------------------------------------------------------- sanitize */

/** Stored UI state is user-writable JSON: never trust its shape, always fall back to defaults. */
export function sanitizeSavedState(raw: unknown): SavedState {
  if (typeof raw !== 'object' || raw === null) {
    return { ...DEFAULT_SAVED_STATE, filters: {}, range: { ...DEFAULT_SAVED_STATE.range } };
  }
  const source = raw as Record<string, unknown>;
  const shared = sanitizeViewFilters(source.filters);
  // The three the collection has no place for: search is session-only here, there is no author
  // scoping, and every post on the screen is saved by construction.
  delete shared.q;
  delete shared.authorIds;
  delete shared.saved;

  const filters: SavedFiltersState = shared;
  const rawFilters =
    typeof source.filters === 'object' && source.filters !== null
      ? (source.filters as Record<string, unknown>)
      : {};
  const listIds = pickIdList(rawFilters.listIds);
  if (listIds) filters.listIds = listIds;
  const tagIds = pickIdList(rawFilters.tagIds);
  if (tagIds) filters.tagIds = tagIds;
  const groupIds = pickIdList(rawFilters.groupIds);
  if (groupIds) filters.groupIds = groupIds;
  const profileIds = pickIdList(rawFilters.profileIds);
  if (profileIds) filters.profileIds = profileIds;
  const savedFrom = pickTimestamp(rawFilters.savedFrom);
  if (savedFrom !== undefined) filters.savedFrom = savedFrom;
  const savedTo = pickTimestamp(rawFilters.savedTo);
  if (savedTo !== undefined) filters.savedTo = savedTo;

  return {
    // `sanitizeRange`/`sanitizeSort` fall back to the *view* defaults, which are not this screen's:
    // an absent key means "never chosen", so the collection's own defaults win.
    range: source.range === undefined ? { ...DEFAULT_SAVED_STATE.range } : sanitizeRange(source.range),
    filters,
    sort: source.sort === undefined ? { ...DEFAULT_SAVED_STATE.sort } : sanitizeSort(source.sort),
  };
}

function pickTimestamp(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : undefined;
}

/** What actually goes to `PUT /api/ui-state/saved:main` (the shape of `SavedUiStateSchema`). */
export function toStoredSavedState(state: SavedState): SavedUiState {
  return { range: state.range, filters: state.filters, sort: state.sort };
}

/* ----------------------------------------------------------------- params */

/**
 * #66 request params. The §5.2 half goes through `toFilterParams` — the same function every other
 * screen uses — so a filter cannot mean one thing here and another there; only the saved-only
 * narrowings and the session-only search term are added.
 */
export function toSavedParams(
  state: SavedState,
  resolved: ResolvedRange,
  search: string,
): Partial<SavedFeedQuery> {
  const params: Partial<SavedFeedQuery> = {
    ...toFilterParams(state.filters, resolved, { isGroup: false }),
    sort: state.sort.key,
    dir: state.sort.dir,
  };
  if (state.filters.listIds?.length) params.listIds = state.filters.listIds;
  if (state.filters.tagIds?.length) params.tagIds = state.filters.tagIds;
  if (state.filters.savedFrom !== undefined) params.savedFrom = state.filters.savedFrom;
  if (state.filters.savedTo !== undefined) params.savedTo = state.filters.savedTo;
  if (state.filters.groupIds?.length) params.groupIds = state.filters.groupIds;
  if (state.filters.profileIds?.length) params.profileIds = state.filters.profileIds;
  const trimmed = search.trim();
  if (trimmed.length > 0) params.q = trimmed;
  return params;
}
